import { Graphics, Text } from 'pixi.js';
import { BaseScene } from './BaseScene.js';
import { GameState } from '../core/GameState.js';
import { GameplayScene } from './GameplayScene.js';
import { ClassSelectScene } from './ClassSelectScene.js';
import { UnlockTreePanel } from '../ui/UnlockTreePanel.js';
import { AchievementPanel } from '../ui/AchievementPanel.js';
import { DebugLog } from '../core/DebugLog.js';
import { GAME_WIDTH, GAME_HEIGHT } from '../main.js';

const MENU_ITEMS = ['New Game', 'Continue', 'Upgrades', 'Achievements'];
const COLOR_SELECTED = 0xffdd44;
const COLOR_NORMAL = 0x88aacc;
const COLOR_DISABLED = 0x444455;

export class MenuScene extends BaseScene {
  init() {
    DebugLog.info('MenuScene', 'Initializing menu');

    // Load meta-progression on menu init
    GameState.loadMeta();

    // Background
    const bg = new Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    bg.fill(0x0a0a0f);
    this.container.addChild(bg);

    // Title
    const title = new Text({
      text: 'ASTRAL CRYPTS',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 20,
        fontWeight: 'bold',
        fill: 0xffdd44,
        letterSpacing: 3,
      }
    });
    title.anchor.set(0.5);
    title.x = GAME_WIDTH / 2;
    title.y = 60;
    this.container.addChild(title);

    // Menu items
    this._selectedIndex = 0;
    this._menuTexts = [];

    const startY = 130;
    const spacing = 24;

    for (let i = 0; i < MENU_ITEMS.length; i++) {
      const text = new Text({
        text: MENU_ITEMS[i],
        style: {
          fontFamily: 'Courier New, monospace',
          fontSize: 12,
          fill: COLOR_NORMAL,
        }
      });
      text.anchor.set(0.5);
      text.x = GAME_WIDTH / 2;
      text.y = startY + i * spacing;
      this.container.addChild(text);
      this._menuTexts.push(text);
    }

    // Save info text (shown below "Continue" when a save exists)
    this._saveInfoText = new Text({
      text: '',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 9,
        fill: 0x667788,
      }
    });
    this._saveInfoText.anchor.set(0.5);
    this._saveInfoText.x = GAME_WIDTH / 2;
    this._saveInfoText.y = startY + 1 * spacing + 14;
    this.container.addChild(this._saveInfoText);

    // Selection cursor (small arrow)
    this._cursor = new Text({
      text: '>',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 12,
        fill: COLOR_SELECTED,
      }
    });
    this._cursor.anchor.set(0.5);
    this.container.addChild(this._cursor);

    // Meta stats display at bottom
    this._metaText = new Text({
      text: '',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 9,
        fill: 0x556677,
      }
    });
    this._metaText.anchor.set(0.5);
    this._metaText.x = GAME_WIDTH / 2;
    this._metaText.y = GAME_HEIGHT - 30;
    this.container.addChild(this._metaText);

    // Unlock tree panel (M27)
    const uiRoot = document.getElementById('ui-root');
    this.unlockTreePanel = new UnlockTreePanel(uiRoot);

    // Achievement panel (M28)
    this.achievementPanel = new AchievementPanel(uiRoot);

    this._updateMenuVisuals();
    this._updateSaveInfo();
    this._updateMetaDisplay();

    DebugLog.info('MenuScene', 'Menu ready');
  }

  _updateMenuVisuals() {
    const hasSave = GameState.hasSave;

    for (let i = 0; i < this._menuTexts.length; i++) {
      const text = this._menuTexts[i];
      const isDisabled = (i === 1 && !hasSave); // Continue disabled if no save

      if (isDisabled) {
        text.style.fill = COLOR_DISABLED;
      } else if (i === this._selectedIndex) {
        text.style.fill = COLOR_SELECTED;
      } else {
        text.style.fill = COLOR_NORMAL;
      }
    }

    // Position cursor next to selected item
    const selected = this._menuTexts[this._selectedIndex];
    this._cursor.x = selected.x - 60;
    this._cursor.y = selected.y;
  }

  /**
   * Show save info under the "Continue" option if a save exists.
   */
  _updateSaveInfo() {
    try {
      const raw = localStorage.getItem('astralcrypts_save');
      if (!raw) {
        this._saveInfoText.text = '';
        return;
      }
      const data = JSON.parse(raw);
      if (!data.isRunActive) {
        this._saveInfoText.text = '';
        return;
      }
      const hp = data.playerHP ?? 100;
      const maxHp = data.playerMaxHP ?? 100;
      const floor = data.floor ?? 1;
      this._saveInfoText.text = `Floor ${floor} - HP: ${hp}/${maxHp}`;
    } catch {
      this._saveInfoText.text = '';
    }
  }

  /**
   * Show meta-progression stats at the bottom of the menu.
   */
  _updateMetaDisplay() {
    if (GameState.totalRuns === 0 && GameState.cryptTokens === 0) {
      this._metaText.text = '';
      return;
    }

    const parts = [];
    if (GameState.cryptTokens > 0) {
      parts.push(`Crypt Tokens: ${GameState.cryptTokens}`);
    }
    if (GameState.totalRuns > 0) {
      parts.push(`Runs: ${GameState.totalRuns}`);
    }
    if (GameState.bestFloor > 0) {
      parts.push(`Best: Floor ${GameState.bestFloor}`);
    }
    this._metaText.text = parts.join('  |  ');
  }

  update(dt) {
    // If unlock panel is open, skip normal menu input
    if (this.unlockTreePanel && this.unlockTreePanel.isOpen) {
      const input = this.sceneManager.inputManager;
      if (input.isKeyJustPressed('Escape')) {
        this.unlockTreePanel.close();
        this._updateMetaDisplay();
      }
      return;
    }

    // If achievement panel is open, skip normal menu input
    if (this.achievementPanel && this.achievementPanel.isOpen) {
      const input = this.sceneManager.inputManager;
      if (input.isKeyJustPressed('Escape')) {
        this.achievementPanel.close();
      }
      return;
    }

    const input = this.sceneManager.inputManager;

    // Navigate menu
    if (input.isKeyJustPressed('ArrowUp') || input.isKeyJustPressed('KeyW')) {
      this._selectedIndex--;
      if (this._selectedIndex < 0) this._selectedIndex = MENU_ITEMS.length - 1;
      this._updateMenuVisuals();
    }

    if (input.isKeyJustPressed('ArrowDown') || input.isKeyJustPressed('KeyS')) {
      this._selectedIndex++;
      if (this._selectedIndex >= MENU_ITEMS.length) this._selectedIndex = 0;
      this._updateMenuVisuals();
    }

    // Confirm selection
    if (input.isKeyJustPressed('Enter') || input.isKeyJustPressed('Space')) {
      this._confirm();
    }
  }

  _confirm() {
    const item = MENU_ITEMS[this._selectedIndex];

    if (item === 'New Game') {
      DebugLog.info('MenuScene', 'Navigating to class selection');
      this.sceneManager.switchTo(new ClassSelectScene(this.sceneManager, this.eventBus));
    } else if (item === 'Continue') {
      if (!GameState.hasSave) {
        DebugLog.info('MenuScene', 'No save data -- cannot continue');
        return;
      }
      DebugLog.info('MenuScene', 'Continuing from save');
      GameState.load();
      GameState.isRunActive = true;
      this.sceneManager.switchTo(new GameplayScene(this.sceneManager, this.eventBus));
    } else if (item === 'Upgrades') {
      DebugLog.info('MenuScene', 'Opening upgrades panel');
      this.unlockTreePanel.open();
    } else if (item === 'Achievements') {
      DebugLog.info('MenuScene', 'Opening achievements panel');
      this.achievementPanel.open();
    }
  }

  destroy() {
    if (this.unlockTreePanel) {
      this.unlockTreePanel.destroy();
      this.unlockTreePanel = null;
    }
    if (this.achievementPanel) {
      this.achievementPanel.destroy();
      this.achievementPanel = null;
    }
    super.destroy();
  }
}
